import React, { useState, useEffect } from "react"
import { useNavigate, useParams, useLocation } from "react-router-dom"
import axiosInstance from "../../../utils/axiosInstance"
import { API_ENDPOINTS } from "../../../constants/config"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Checkbox } from "@/components/ui/checkbox"
import { ArrowLeft, Loader2 } from "lucide-react"

function SectionForm({ classes, initialData, onSubmit, onCancel, loading }) {
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    selectedClasses: initialData?.classes?.map((cls) => cls._id) || [],
  })
  const [error, setError] = useState(null)

  const handleClassToggle = (classId) => {
    setFormData((prev) => ({
      ...prev,
      selectedClasses: prev.selectedClasses.includes(classId)
        ? prev.selectedClasses.filter((id) => id !== classId)
        : [...prev.selectedClasses, classId],
    }))
  }

  const handleNameChange = (e) => {
    const value = e.target.value.toUpperCase()
    if (value.length <= 3 && /^[A-Z]*$/.test(value)) {
      setFormData((prev) => ({ ...prev, name: value }))
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!formData.name) { setError("Please enter a section name"); return }
    if (!/^[A-Z]{1,3}$/.test(formData.name)) { setError("Section name must be 1-3 capital letters (A-Z)"); return }
    if (formData.selectedClasses.length === 0) { setError("Please select at least one class"); return }
    setError(null)
    onSubmit(formData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && (
        <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>
      )}
      <div>
        <label className="block mb-2 text-sm font-medium text-gray-700">Section Name</label>
        <Input
          type="text"
          value={formData.name}
          onChange={handleNameChange}
          placeholder="Enter section name (e.g., A, AB, ABC)"
          maxLength={3}
          className="uppercase max-w-xs"
          disabled={loading}
        />
        <p className="text-xs text-gray-500 mt-1">Enter 1-3 capital letters (A-Z)</p>
      </div>

      <div>
        <label className="block mb-3 text-sm font-medium text-gray-700">Select Classes</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
          {classes.map((cls) => (
            <div key={cls._id} className="flex items-center space-x-2">
              <Checkbox
                id={`class-${cls._id}`}
                checked={formData.selectedClasses.includes(cls._id)}
                onCheckedChange={() => handleClassToggle(cls._id)}
                disabled={loading}
              />
              <label htmlFor={`class-${cls._id}`} className="text-sm font-medium leading-none cursor-pointer">
                {cls.name}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3 pt-2">
        <Button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white" disabled={loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Update Section
        </Button>
        <Button type="button" variant="outline" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
      </div>
    </form>
  )
}

export default function EditSectionPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()

  const [section, setSection] = useState(location.state?.section || null)
  const [classes, setClasses] = useState([])
  const [loading, setLoading] = useState(false)
  const [fetchLoading, setFetchLoading] = useState(!section)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setFetchLoading(true)
        const [classesRes, ...rest] = await Promise.all([
          axiosInstance.get(API_ENDPOINTS.classes),
          ...(!section ? [axiosInstance.get(API_ENDPOINTS.section(id))] : []),
        ])
        setClasses(classesRes.data.data.classes)
        if (!section && rest[0]) {
          setSection(rest[0].data.data)
        }
      } catch {
        setError("Failed to load section data")
      } finally {
        setFetchLoading(false)
      }
    }
    fetchData()
  }, [id])

  const handleSubmit = async (formData) => {
    try {
      setLoading(true)
      await axiosInstance.patch(API_ENDPOINTS.section(id), {
        name: formData.name,
        classes: formData.selectedClasses,
      })
      navigate("/dashboard/settings/sections")
    } catch (err) {
      setError(err.response?.data?.message || "Failed to update section")
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    navigate("/dashboard/settings/sections")
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            className="h-9 w-9 rounded-full"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Edit Section</h1>
            <p className="text-sm text-gray-500 mt-0.5">Update section name and assigned classes</p>
          </div>
        </div>

        <Card className="border border-gray-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-base font-medium text-gray-900">
              {section ? `Editing: ${section.name}` : "Section Details"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {fetchLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                <span className="text-sm">Loading...</span>
              </div>
            ) : error ? (
              <div className="bg-red-50 text-red-600 p-3 rounded-md text-sm">{error}</div>
            ) : (
              <SectionForm
                classes={classes}
                initialData={section}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                loading={loading}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
